import React, { useState, useEffect, useCallback } from 'react';
import apiClient from '../../../api/apiClient';
import LoadingSpinner from '../../../components/ui/LoadingSpinner';
import ErrorBanner from '../../../components/ui/ErrorBanner';
import { st, colors } from '../shared/meetingStyles';
import Avatar from '../shared/Avatar';
import { CheckSquareIcon, XIcon } from '../shared/Icons';

export default function TodosSection({ sessionId, meetingId }) {
  const [todos, setTodos] = useState([]);
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newTitle, setNewTitle] = useState('');
  const [newOwner, setNewOwner] = useState('');
  const [newDue, setNewDue] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [todosRes, teamRes] = await Promise.all([
        apiClient.get('/meetings/todos?completed=false'),
        apiClient.get('/meetings/team'),
      ]);
      setTodos(todosRes.data.todos || []);
      setTeam(teamRes.data.users || []);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const addTodo = async (e) => {
    e.preventDefault();
    if (!newTitle.trim()) return;
    await apiClient.post('/meetings/todos', {
      title: newTitle.trim(), owner_user_id: newOwner || null, due_date: newDue || null,
      meeting_id: meetingId, session_id: sessionId,
    });
    setNewTitle(''); setNewOwner(''); setNewDue('');
    await load();
  };

  const complete = async (id) => { await apiClient.put(`/meetings/todos/${id}/complete`, { session_id: sessionId }); await load(); };
  const remove = async (id) => {
    if (!window.confirm('Delete this to-do?')) return;
    await apiClient.delete(`/meetings/todos/${id}`); await load();
  };

  if (loading) return <LoadingSpinner />;

  const isOverdue = (dueDate) => dueDate && dueDate < new Date().toISOString().slice(0, 10);

  return (
    <div style={st.card}>
      {error && <ErrorBanner message={error} />}
      <div style={st.cardHeader}>
        <h2 style={{ ...st.h2, display: 'flex', alignItems: 'center', gap: '0.5rem' }}><CheckSquareIcon size={18} /> To-dos</h2>
        <span style={st.pill}>{todos.length} open</span>
      </div>
      <form onSubmit={addTodo} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.9rem', flexWrap: 'wrap', padding: '0.85rem 1rem', background: colors.bgSubtle, borderRadius: '0.75rem', border: `1.5px solid ${colors.border}` }}>
        <select value={newOwner} onChange={e => setNewOwner(e.target.value)} style={{ ...st.input, width: '150px' }}>
          <option value="">No owner</option>
          {team.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
        </select>
        <input value={newTitle} onChange={e => setNewTitle(e.target.value)} placeholder="Create a quick To-do" style={{ ...st.input, flex: 1, minWidth: '200px' }} />
        <input type="date" value={newDue} onChange={e => setNewDue(e.target.value)} style={st.input} />
        <button type="submit" style={st.primaryBtn}>Add</button>
      </form>
      {todos.length === 0 ? (
        <div style={st.emptyState}><CheckSquareIcon size={28} style={{ color: colors.faint }} />No open to-dos.</div>
      ) : (
        todos.map(todo => (
          <div key={todo.id} style={{ display: 'flex', alignItems: 'center', gap: '0.7rem', padding: '0.6rem 0.25rem', borderBottom: '1px solid #f1f5f9' }}>
            <input type="checkbox" onChange={() => complete(todo.id)} style={{ width: '16px', height: '16px', cursor: 'pointer' }} />
            <Avatar name={todo.owner_name} size={22} />
            <span style={{ flex: 1, fontSize: '0.8rem', color: colors.text, fontWeight: '500' }}>{todo.title}</span>
            {todo.due_date && (
              <span style={{
                fontSize: '0.68rem', fontWeight: '700', padding: '0.2rem 0.55rem', borderRadius: '999px',
                color: isOverdue(todo.due_date) ? colors.red : colors.faint,
                background: isOverdue(todo.due_date) ? colors.redSoft : colors.bgSubtle,
              }}>{todo.due_date}</span>
            )}
            <button onClick={() => remove(todo.id)} style={st.ghostBtn}><XIcon size={13} /></button>
          </div>
        ))
      )}
    </div>
  );
}
